import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EllipsisVerticalIcon, PlusIcon } from 'lucide-react'
import {
	Avatar, AvatarFallback, AvatarImage,
	Badge,
	Button,
	Checkbox,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Input,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import type { Task, TaskKind } from '@/types'
import {
	useCreateTaskMutation,
	useDeleteTaskMutation,
	useListTasksQuery,
	useUpdateTaskMutation,
} from '@/api'
import { TaskFormDialog } from '@/components/task-form-dialog'

const TASK_KINDS: TaskKind[] = ['CALL', 'MEETING', 'EMAIL', 'OTHER']

function formatDue(iso: string): string {
	const date = new Date(iso)
	const today = new Date()
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	if (date.toDateString() === today.toDateString()) return `Сегодня в ${time}`
	if (date.toDateString() === tomorrow.toDateString()) return `Завтра в ${time}`
	return `${date.toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'short' })} в ${time}`
}

function dueVariant(iso: string): 'destructive' | 'warning' {
	const hoursUntil = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60)
	return hoursUntil < 24 ? 'destructive' : 'warning'
}

interface RentalTasksSectionProps {
	rentalId:       string
	onCreateTask?:  () => void
}

// Unified tasks only (feedback #286): the rental card reads and writes the
// global Task entity with a RENTAL binding — the legacy RentalTask rows were
// migrated in 20260731140000_unify_tasks.
export function RentalTasksSection({ rentalId, onCreateTask }: RentalTasksSectionProps) {
	const { t } = useTranslation()
	const [taskToEdit,    setTaskToEdit]    = useState<Task | undefined>(undefined)
	const [taskFormOpen,  setTaskFormOpen]  = useState(false)
	const [quickOpen,     setQuickOpen]     = useState(false)
	const [quickKind,     setQuickKind]     = useState<TaskKind>('CALL')
	const [quickDesc,     setQuickDesc]     = useState('')
	const [quickDue,      setQuickDue]      = useState('')

	const { data } = useListTasksQuery({ bindingKind: 'RENTAL', bindingId: rentalId, limit: 50 })
	const [createTask, { isLoading: isCreating }] = useCreateTaskMutation()
	const [updateTask] = useUpdateTaskMutation()
	const [deleteTask] = useDeleteTaskMutation()

	const tasks: Task[] = data?.items ?? []

	async function handleQuickCreate() {
		if (!quickDesc.trim()) return
		await createTask({
			kind:        quickKind,
			description: quickDesc.trim(),
			dueDate:     quickDue ? new Date(quickDue).toISOString() : undefined,
			binding:     { kind: 'RENTAL', id: rentalId },
		})
		setQuickDesc('')
		setQuickDue('')
		setQuickOpen(false)
	}

	function handleCreate() {
		setTaskToEdit(undefined)
		if (onCreateTask) onCreateTask()
		else setTaskFormOpen(true)
	}

	return (
		<>
			{tasks.length === 0 ? (
				<div className="flex flex-col items-center gap-3 py-8">
					<p className="text-sm text-(--color-muted-foreground)">{t('rent.tasks.empty')}</p>
					<Button size="sm" variant="secondary" iconLeft={<PlusIcon className="size-4" />} onClick={handleCreate}>
						{t('tasks.create')}
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-3">
					{tasks.map((task) => (
						<TaskCard
							key={task.id}
							task={task}
							onToggle={(isDone) => void updateTask({ id: task.id, body: { isDone } })}
							onEdit={() => { setTaskToEdit(task); setTaskFormOpen(true) }}
							onDelete={() => void deleteTask(task.id)}
							t={t}
						/>
					))}

					{quickOpen ? (
						<div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) p-4">
							<select
								value={quickKind}
								onChange={(e) => setQuickKind(e.target.value as TaskKind)}
								className="rounded-(--radius-sm) border border-(--color-border) bg-(--color-background) px-2 py-1.5 text-sm text-(--color-foreground)"
							>
								{TASK_KINDS.map((kind) => (
									<option key={kind} value={kind}>{t(`tasks.kinds.${kind}`)}</option>
								))}
							</select>
							<Input
								value={quickDesc}
								onChange={(e) => setQuickDesc(e.target.value)}
								placeholder={t('rent.tasks.fields.description')}
								autoFocus
							/>
							<Input
								type="datetime-local"
								value={quickDue}
								onChange={(e) => setQuickDue(e.target.value)}
							/>
							<div className="flex justify-end gap-2">
								<Button variant="secondary" size="sm" onClick={() => setQuickOpen(false)}>
									{t('common.cancel')}
								</Button>
								<Button size="sm" disabled={!quickDesc.trim() || isCreating} loading={isCreating} onClick={() => void handleQuickCreate()}>
									{t('common.save')}
								</Button>
							</div>
						</div>
					) : (
						<Button size="sm" variant="ghost" iconLeft={<PlusIcon className="size-4" />} className="self-start" onClick={() => setQuickOpen(true)}>
							{t('rent.tasks.quickCreate', { defaultValue: 'Добавить задачу по аренде' })}
						</Button>
					)}
				</div>
			)}

			<TaskFormDialog
				open={taskFormOpen}
				onOpenChange={setTaskFormOpen}
				task={taskToEdit}
				initialBinding={{ kind: 'RENTAL', id: rentalId }}
				lockBinding
			/>
		</>
	)
}

function TaskCard({
	task, onToggle, onEdit, onDelete, t,
}: {
	task:     Task
	onToggle: (isDone: boolean) => void
	onEdit:   () => void
	onDelete: () => void
	t:        (k: string) => string
}) {
	const isOther = task.kind === 'OTHER'
	const title =
		task.title ??
		(isOther && task.description ? task.description : t(`tasks.kinds.${task.kind}`))
	const showDescription = !!task.description && (!!task.title || !isOther)

	return (
		<div className="rounded-(--radius-md) border border-(--color-border) p-4">
			<div className="flex items-center gap-3">
				<Checkbox
					checked={task.isDone}
					onCheckedChange={(v) => onToggle(Boolean(v))}
				/>
				<h3 className="flex-1 text-base font-[500] leading-6 text-(--color-foreground)">
					{title}
				</h3>
				<DropdownMenu>
					<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.tasks.actions.menu')} />}>
						<EllipsisVerticalIcon className="size-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="min-w-40">
						<DropdownMenuItem onClick={onEdit}>{t('rent.tasks.actions.edit')}</DropdownMenuItem>
						<DropdownMenuItem variant="destructive" onClick={onDelete}>
							{t('rent.tasks.actions.delete')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<div className="mt-3 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
				{showDescription && (
					<>
						<span className="text-(--color-muted-foreground)">{t('rent.tasks.fields.description')}:</span>
						<span className="text-(--color-foreground)">{task.description}</span>
					</>
				)}
				{task.responsibleName && (
					<>
						<span className="text-(--color-muted-foreground)">{t('rent.tasks.fields.responsible')}:</span>
						<span className="flex items-center gap-2 text-(--color-foreground)">
							<Avatar className="size-5 rounded-full">
								{task.responsibleAvatar && <AvatarImage src={task.responsibleAvatar} alt={task.responsibleName} />}
								<AvatarFallback className="text-xs">{task.responsibleName.charAt(0)}</AvatarFallback>
							</Avatar>
							{task.responsibleName}
						</span>
					</>
				)}
				{task.dueDate && (
					<>
						<span className="text-(--color-muted-foreground)">{t('rent.tasks.fields.due')}:</span>
						<span>
							<Badge variant={dueVariant(task.dueDate)} size="sm">
								{formatDue(task.dueDate)}
							</Badge>
						</span>
					</>
				)}
			</div>
		</div>
	)
}
